import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  FormGroup,
  Form,
  Input,
  Button,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { successToast } from "components/toast";
import { errorToast } from "components/toast";
import { useNavigate, useParams } from "react-router-dom";
import { createUser } from "services/_user";
import { getUserDataById } from "services/_user";
import { updateUserDataById } from "services/_user";

const CreateUser = () => {
  const { id } = useParams();
  const [editData, setEditData] = useState({});
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/admin/users");
  };

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      name: values.name,
      email: values.email,
      status: values.status,
      phone_no: values.phone_no,
      password: values.password,
    };

    if (id) {
      const updatePayload = {
        name: values.name,
        email: values.email,
        status: values.status,
        phone_no: values.phone_no,
      };
      const data = await updateUserDataById(id, updatePayload);
      if (data.data) {
        successToast(data.message);
        navigate("/admin/users");
      } else {
        errorToast(data.message);
      }
    } else {
      const data = await createUser(payload);
      if (data.data) {
        successToast(data.message);
        resetForm();
      } else {
        errorToast(data.message);
      }
    }
  };

  const getInitialData = useCallback(async () => {
    const data = await getUserDataById(id);
    if (data.data) {
      setEditData(data.data);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getInitialData();
    }
  }, [getInitialData, id]);

  const createSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .required("Email is required")
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format"),
    phone_no: Yup.string()
      .matches(
        /^\d{10}$/,
        "Phone number must be exactly 10 digits and contain only numbers."
      )
      .optional(),
    status: Yup.boolean().nullable(),
    password: Yup.string()
      .required("Password is required")
      .matches(
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/,
        "Password must be at least 8 characters, include 1 uppercase letter, 1 number, and 1 special character"
      ),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });

  const updateSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().required("Email is required"),
    phone_no: Yup.string()
      .matches(
        /^\d{10}$/,
        "Phone number must be exactly 10 digits and contain only numbers."
      )
      .optional(),
    status: Yup.boolean().nullable(),
    password: Yup.string().optional(),
    confirmPassword: Yup.string().optional(),
  });

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col className="order-xl-1">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">
                      <i
                        className="fas fa-arrow-left"
                        style={{ paddingRight: "10px" }}
                        onClick={handleNavigate}
                      ></i>
                      {id ? "Edit user" : "Create user "}
                    </h3>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Formik
                  enableReinitialize={true}
                  initialValues={{
                    name: editData?.name || "",
                    email: editData?.email || "",
                    status: editData?.status ?? null,
                    phone_no: editData?.phone_no || "",
                    password: "",
                    confirmPassword: "",
                  }}
                  validationSchema={!id ? createSchema : updateSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, handleChange, handleSubmit }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="pl-lg-4">
                        <Row>
                          {/* Name */}
                          <Col lg="4">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-name"
                              >
                                Name <span style={{ color: "red" }}>*</span>
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-name"
                                name="name"
                                placeholder="Enter name"
                                type="text"
                                value={values.name}
                                onChange={handleChange}
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="4">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-email"
                              >
                                Email<span style={{ color: "red" }}> *</span>
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-email"
                                name="email"
                                placeholder="Enter email"
                                type="text"
                                value={values.email}
                                onChange={handleChange}
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="4">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-phone_no"
                              >
                                phone no
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-phone_no"
                                name="phone_no"
                                placeholder="Enter phone no"
                                type="text"
                                value={values.phone_no}
                                onChange={handleChange}
                              />
                              <ErrorMessage
                                name="phone_no"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          {!id ? (
                            <>
                              {" "}
                              <Col lg="4">
                                <FormGroup>
                                  <label
                                    className="form-control-label"
                                    htmlFor="input-password"
                                  >
                                    Password
                                    <span style={{ color: "red" }}> *</span>
                                  </label>
                                  <Input
                                    className="form-control-alternative"
                                    id="input-password"
                                    name="password"
                                    placeholder="Enter password"
                                    type="text"
                                    value={values.password}
                                    onChange={handleChange}
                                  />
                                  <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="text-danger"
                                  />
                                </FormGroup>
                              </Col>
                              <Col lg="4">
                                <FormGroup>
                                  <label
                                    className="form-control-label"
                                    htmlFor="input-confirmPassword"
                                  >
                                    confirm Password
                                    <span style={{ color: "red" }}> *</span>
                                  </label>
                                  <Input
                                    className="form-control-alternative"
                                    id="input-confirmPassword"
                                    name="confirmPassword"
                                    placeholder="Enter confirm Password"
                                    type="text"
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                  />
                                  <ErrorMessage
                                    name="confirmPassword"
                                    component="div"
                                    className="text-danger"
                                  />
                                </FormGroup>
                              </Col>{" "}
                            </>
                          ) : (
                            <></>
                          )}

                          <Col lg="4">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-status"
                              >
                                Status
                              </label>
                              <Input
                                type="select"
                                id="input-status"
                                name="status"
                                className="form-control-alternative"
                                style={{
                                  boxShadow:
                                    "0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)",
                                  border: 0,
                                  color: "#90a4ae !important",
                                }}
                                value={
                                  values.status === true
                                    ? "true"
                                    : values.status === false
                                    ? "false"
                                    : ""
                                }
                                onChange={(e) =>
                                  setFieldValue(
                                    "status",
                                    e.target.value === "true" ? true : false
                                  )
                                }
                              >
                                <option value="">Select status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                              </Input>
                              <ErrorMessage
                                name="status"
                                component="div"
                                className="text-danger"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>

                      {/* Submit */}
                      <div className="pl-lg-4">
                        <Button color="primary" type="submit">
                          Save
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CreateUser;
